import React, { useState, useEffect, useRef } from "react";
import { Modal, Row, Form, Tabs, Tab } from "react-bootstrap";
import styles from "./ClientCreator.module.css";
import Select from "react-select";
import { SingleValue } from "react-select";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import {
  handleClientSaveAndPrefill,
  loadClientForms,
  prefillRemainingTabsFromClientDetails,
  saveClientData,
} from "./ClientCreator.utils";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import {
  FormRecord,
  ClientCreatorProps,
  handleClose,
} from "./ClientCreator.utils";
import CygnusForm from "../../../Reusables/components/CygnusForm/CygnusForm";
import { ThreeCircles } from "react-loader-spinner";
import DocumentUploader from "./components/DocumentUploader/DocumentUploader";
import { AlertModal } from "../../../Reusables/components/AlertModal/AlertModal";
import { getFormFlow } from "../ClientsGrid/ClientsGrid.utils";
import { fetchClientData } from "../ClientEditor/ClientEditor.service";
import { patchFieldsWithClientData, DirtyApi } from "./ClientCreator.utils";
import { sendForApproval } from "../ClientsGrid/ClientsGrid.service";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import ApprovalSettingsModal from "../ApproverSettingModal/ApproverSettingModal";
// use your services (you pasted them) to fetch/save
import { fetchExistingApprovers } from "../ApproverSettingModal/ApproverSettingModal.service";
import type { Approver } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";
import { COI_REGISTRY_PRIVILEGE_CODE } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";
import { fetchRuleApproverPrivileges } from "../../firm_setup/RulesModal/RulesModal.service";
import { getApproversList } from "./ClientCreator.service";
import MatchedSSNEINClients from "./components/MatchedSSNEINClients/MatchedSSNEINClients";

const ClientCreator: React.FC<ClientCreatorProps> = ({
  show,
  onHide,
  refresh,
  purpose,
}) => {
  const formFlowConfig = getFormFlow(purpose, []);
  // console.log('purpose?.value:', purpose?.value, '| formFlowConfig:', formFlowConfig);  
  type TabKey = (typeof formFlowConfig)[number]["key"];

  const orderedFlow = [...formFlowConfig]
    .filter((s) => s.enabled)
    .sort((a, b) => a.order - b.order);
  const firstTabKey = orderedFlow[0]?.key as TabKey;

  const createInitialTabState = <T,>(initialValue: T): Record<TabKey, T> => {
    return formFlowConfig.reduce((acc, step) => {
      acc[step.key as TabKey] = initialValue;
      return acc;
    }, {} as Record<TabKey, T>);
  };

  const [activeTab, setActiveTab] = useState<TabKey>(formFlowConfig[0]?.key);
  const [formRecordsByTab, setFormRecordsByTab] = useState(
    createInitialTabState<FormRecord[]>([])
  );
  const [selectedFormByTab, setSelectedFormByTab] = useState(
    createInitialTabState<FormRecord | null>(null)
  );
  const [formFieldsByTab, setFormFieldsByTab] = useState(
    createInitialTabState<FieldProps[]>([])
  );
  const [formOptionsByTab, setFormOptionsByTab] = useState(
    createInitialTabState<{ label: string; value: number }[]>([])
  );
  const [clientUpdateApiProgress, setClientUpdateApiProgress] =
    useState<boolean>(false);
  const [clientDetails, setClientDetails] = useState<any>();
  const [fetchClientApiProgress, setFetchClientApiProgress] =
    useState<boolean>(false);
  const [approvalApiProgress, setApproalApiProgress] = useState<boolean>(false);
  const [formApiProgress, setFormApiProgress] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean | null>(null);
  const [clientMasterId, setClientMasterId] = useState<number>(0);
  const [existingClientId, setExistingClientId] = useState<number>(0);
  const [warningModal, setWarningModal] = useState<boolean>(false);
  const [enabledTabs, setEnabledTabs] = useState<TabKey[]>([
    `${formFlowConfig[0].key}`,
  ]);
  const [onDemandValidation, setOnDemandValidation] = useState<boolean>(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [showSSNEINModal, setShowSSNEINModal] = useState(false);
  const [clientsMatchMessage, setClientsMatchMessage] = useState(null);
  const [clientsMatchedData, setClientsMatchedData] = useState([]);
  const [continueSavingClient, setContinueSavingClient] = useState(false);
  const [showMatchedSSNEINModal, setShowMatchedSSNEINModal] = useState(false);

  const [approvalSettingModal, setApprovalSettingModal] = useState(false);
  const [clientTypeCode, setClientTypeCode] = useState<string>(""); // if your modal needs it
  const [firstApprover, setFirstApprover] = useState<Approver | null>(null);
  const [fetchApproversProgress, setFetchApproversProgress] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [coiApproverGroupLabel, setCoiApproverGroupLabel] = useState<string>();

  useEffect(() => {
    fetchRuleApproverPrivileges().then((options) => {
      const coiOption = options.find((o) => o.value === COI_REGISTRY_PRIVILEGE_CODE);
      if (coiOption) setCoiApproverGroupLabel(coiOption.label);
    });
  }, []);

  // Document validation state
  const [documentsValid, setDocumentsValid] = useState<boolean>(true);
  const [missingDocuments, setMissingDocuments] = useState<string[]>([]);
  const [showDocumentWarning, setShowDocumentWarning] = useState<boolean>(false);

  const handleDocumentValidationChange = (result: { isValid: boolean; missing: string[] }) => {
    setDocumentsValid(result.isValid);
    setMissingDocuments(result.missing);
  };

  // Identify the ClientTypeCode field in current tab's fields
  const getClientTypeCodeFromFields = (): string => {
    const fields = formFieldsByTab[activeTab] ?? [];
    const f = fields.find((ff) => {
      const code = String(
        ff.allfields_code ?? ff.allfields_key ?? ff.label ?? ""
      ).toLowerCase();
      return code === "clienttypecode";
    });
    return String(f?.value ?? "").trim();
  };

  const refreshApproverBadge = async () => {
    if (!clientMasterId) {
      setFirstApprover(null);
      return;
    }
    setFetchApproversProgress(true);
    try {
      const ctc = getClientTypeCodeFromFields();
      const list = await getApproversList(clientMasterId, ctc);

      const first = list[0];
      const display =
        first?.COIApprover === "Y"
          ? coiApproverGroupLabel || "Reviewers"
          : first?.FirstName || first?.LastName
          ? `${first?.FirstName ?? ""} ${first?.LastName ?? ""}`.trim()
          : first?.UserName ?? first?.Email ?? "";

      // If approval is disabled, show “Not required”
      const approvalOff =
        String(first?.ApprovalRequired ?? "").toUpperCase() === "N";
      setFirstApprover(
        approvalOff || !first
          ? null
          : { ...first, Displayname: display || "Not set" }
      );
    } finally {
      setFetchApproversProgress(false);
    }
  };

  useEffect(() => {
    if (clientMasterId) refreshApproverBadge();
    else setFirstApprover(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMasterId, coiApproverGroupLabel]);

  useEffect(() => {
    console.log("FIRST APPROVER: ", firstApprover);
  }, [firstApprover]);

  const openApprovalSettings = () => {
    // If modal needs type code, resolve it once here
    const ctc = getClientTypeCodeFromFields();
    setClientTypeCode(ctc);
    setApprovalSettingModal(true);
  };

  useEffect(() => {
    if (!show) return;
    loadClientForms(formFlowConfig, setFormRecordsByTab, setFormApiProgress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    const options =
      formRecordsByTab[activeTab]?.map((form) => ({
        value: form.FirmFormId,
        label: form.FormName,
      })) ?? [];
    setFormOptionsByTab((prev) => ({ ...prev, [activeTab]: options }));
  }, [activeTab, formRecordsByTab]);

  useEffect(() => {
    if (!show) return;

    formFlowConfig.forEach(({ key }) => {
      const records = formRecordsByTab[key];
      const fallback = records?.[0];
      if (!selectedFormByTab[key] && fallback) {
        setSelectedFormByTab((prev) => ({ ...prev, [key]: fallback }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, formRecordsByTab]);

  // Identify the LOS field by key/code/label
  const isLOSField = (f: FieldProps) => {
    const key = String(f.allfields_code ?? "").toLowerCase();
    return key === "business_unit";
  };

  // Pick the value to write from purpose
  const losFromPurpose = (
    p?: { value?: any; id?: any; label?: string } | null
  ) => String(p?.value ?? "");

  useEffect(() => {
    Object.entries(selectedFormByTab).forEach(([tabKey, form]) => {
      if (!form?.InputStructure?.fields) return;

      setFormFieldsByTab((prev) => {
        const losValue = losFromPurpose(purpose);

        const filledFields = form.InputStructure.fields.map(
          (field: FieldProps) => {
            // baseline: keep provided value or empty
            const base = { ...field, value: field.value ?? "" };

            // if this is the Line of Service field, overwrite its value once
            if (isLOSField(field)) {
              const next: FieldProps = { ...base, value: losValue };

              // OPTIONAL (one-liner): if it's a select and the option isn't present, add it so UI shows the label
              if (field.type === "select") {
                const options = field.options ?? [];
                const existing = options.find(
                  (o) => String(o.value).trim().toLowerCase() === losValue.trim().toLowerCase()
                );

                if (existing) {
                  next.value = String(existing.value);
                } else {
                  next.options = [{ id: losValue, value: losValue, label: String(purpose?.label ?? losValue) }, ...options];
                  next.allOptions = next.options;
                }
              }

              return next;
            }
            return base;
          }
        );

        return {
          ...prev,
          [tabKey]: filledFields,
        };
      });
    });
  }, [selectedFormByTab, purpose]);

  const handleTabSelect = async (key: any) => {
    if (!key) return;
    if (enabledTabs.includes(key)) {
      setActiveTab(key as TabKey);
      if (clientMasterId) {
        await prefillRemainingTabsFromClientDetails(
          clientMasterId,
          activeTab,
          formFieldsByTab,
          selectedFormByTab,
          setFormFieldsByTab,
          setClientDetails,
          setFetchClientApiProgress
        );
      }
    }
  };

  const handleFormSelect = (
    selected: SingleValue<{ value: number; label: string }>
  ) => {
    const selectedForm = formRecordsByTab[activeTab].find(
      (record) => record.FirmFormId === selected?.value
    );
    if (selectedForm) {
      setSelectedFormByTab((prev) => ({ ...prev, [activeTab]: selectedForm }));
    }
  };

  const updateFormFields = (fields: FieldProps[]) => {
    setFormFieldsByTab((prev) => ({ ...prev, [activeTab]: fields }));
  };

  const enableNextTab = async (currentTab: TabKey, activate: boolean = true) => {
    // Use the *ordered* flow so “next” is predictable
    const idx = orderedFlow.findIndex((t) => t.key === currentTab);
    const next = orderedFlow[idx + 1];
    if (!next) return;

    // Enable the next tab (if not already)
    setEnabledTabs((prev) =>
      prev.includes(next.key) ? prev : [...prev, next.key]
    );

    // Immediately switch to it (programmatic switch ignores the disabled prop)
    if (activate) setActiveTab(next.key as TabKey);
    if (clientMasterId) {
      await prefillRemainingTabsFromClientDetails(
        clientMasterId,
        activeTab,
        formFieldsByTab,
        selectedFormByTab,
        setFormFieldsByTab,
        setClientDetails,
        setFetchClientApiProgress
      );
    }
  };

  const handleOnDemandValidationComplete = async (isValid: boolean) => {
    if (!onDemandValidation) return;

    // Reset immediately so the next tab does not auto-process
    console.log(onDemandValidation);
    setOnDemandValidation(false);

    if (!isValid) {
      setWarningModal(true);
      setFetchClientApiProgress(false);
      return;
    }

    const isCreateStep = activeTab === firstTabKey;
    const shouldCreate = isCreateStep && !clientMasterId;

    await handleClientSaveAndPrefill({
      activeTab,
      purpose,
      clientMasterId,
      isCreate: shouldCreate,
      setClientMasterId,
      formFieldsByTab,
      selectedFormByTab,
      setFormFieldsByTab,
      setClientUpdateApiProgress,
      setClientDetails,
      setFetchClientApiProgress,
      onHide,
      enableNextTab,
      setOnDemandValidation,
      setShowSSNEINModal,
      setClientsMatchMessage,
      setClientsMatchedData,
      continueSavingClient,
      setShowMatchedSSNEINModal
    });
  };

  // debounce bucket
  const fieldEventTimerRef = React.useRef<number | undefined>(undefined);

  // 1) event routing
  const fieldEventHandlers: Record<
    string,
    (args: { value: any; field: FieldProps }) => Promise<void> | void
  > = {
    // inside ClientCreator fieldEventHandlers
    "client.prefill": async ({ value }) => {
      if (!value) return;

      if (fieldEventTimerRef.current) clearTimeout(fieldEventTimerRef.current);
      fieldEventTimerRef.current = window.setTimeout(async () => {
        setFormApiProgress(true);
        try {
          const res = await fetchClientData(
            value,
            setClientDetails,
            setFormApiProgress,
            true
          );

          if (!res?.success) {
            // API already gave you the message
            setLookupError(
              res?.message || `Client Id: ${value} does not exist`
            );
            setShowLookupModal(true);
            return;
          }

          // success path: res.data[0] was set into clientDetails already
          const details = Array.isArray(res.data) ? res.data[0] : undefined;
          if (!details) return;

          const dirtyIds = dirtyApiRef.current?.get() ?? [];
          setFormFieldsByTab((prev) => {
            const current = prev[activeTab] ?? [];
            const patched = patchFieldsWithClientData(current, details, {
              skip: { byId: dirtyIds, byKey: ["business_unit"] },
              overwriteExisting: true
            });
            return { ...prev, [activeTab]: patched };
          });
        } finally {
        }
      }, 150);
    },
  };

  // 2) single entry point from CygnusForm
  const handleFieldEventFromForm = ({
    eventKey,
    value,
    field,
  }: {
    eventKey?: string;
    value: any;
    field: FieldProps;
  }) => {
    const key = eventKey ?? "default";
    const handler = fieldEventHandlers[key];
    if (handler) handler({ value, field });
  };

  const dirtyApiRef = useRef<DirtyApi | null>(null);

  const [dirtyByTab, setDirtyByTab] = useState<Record<TabKey, boolean>>(
    createInitialTabState<boolean>(false)
  );

  const hasUnsavedChanges = React.useMemo(() => {
    const enabledKeys = formFlowConfig.filter((s) => s.enabled).map((s) => s.key as TabKey);
    return enabledKeys.some((k) => k !== "DOCUMENTS" && dirtyByTab[k] === true);
  }, [dirtyByTab, formFlowConfig]);

  const handleAttemptClose = () => {
    const dirtyIds = dirtyApiRef.current?.get?.() ?? [];
    const isDirtyNow = dirtyIds.length > 0 || hasUnsavedChanges;
    if (isDirtyNow) setShowAlert(true);
    else {
      setShowAlert(false);
      handleClose(
        () => setFormFieldsByTab(createInitialTabState([])),
        () => setSelectedFormByTab(createInitialTabState(null)),
        refresh,
        onHide
      );
    }
  };

  useEffect(() => {
    if (!show) return;

    setShowAlert(false);
    setDirtyByTab(createInitialTabState(false));
    // Optional: also reset enabled tabs + active tab if you want a fully fresh open
    // setEnabledTabs([`${formFlowConfig[0].key}` as TabKey]);
    // setActiveTab(formFlowConfig[0]?.key as TabKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);


  return (
    <>
      <AlertModal
        show={warningModal}
        onHide={() => setWarningModal(false)}
        title="Error in the form"
        message={"Incorrect / empty required fields data."}
        type="warning"
        listItems={[]}
      />
      <AlertModal
        show={showLookupModal}
        onHide={() => setShowLookupModal(false)}
        title="Client not found"
        message={lookupError ?? "The client id you entered does not exist."}
        type="warning"
        listItems={[]}
      />
      <AlertModal
        show={showDocumentWarning}
        onHide={() => setShowDocumentWarning(false)}
        title="Missing Required Documents"
        message="Please upload the following required documents before sending for approval:"
        type="warning"
        listItems={missingDocuments}
      />
      {approvalSettingModal && (
        <ApprovalSettingsModal
          title="Approval Setting"
          clientMasterId={clientMasterId}
          clientTypeCode={clientTypeCode}
          show={approvalSettingModal}
          onHide={() => setApprovalSettingModal(false)}
          onUpdated={async () => {
            await refreshApproverBadge(); // ← refresh after save lands
            setApprovalSettingModal(false);
          }}
          onApproversRefreshed={refreshApproverBadge}
          disableCheck={true}
        />
      )}

      <AlertModal
        show={showAlert}
        onHide={() => setShowAlert(false)}
        title="Are you sure?"
        message="Closing this will discard all the unsaved data. Do you want to continue?"
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: () => setShowAlert(false) },
          {
            label: "Continue", onClick: () => {
              setDirtyByTab(createInitialTabState(false));
              setShowAlert(false);
              handleClose(
                () => setFormFieldsByTab(createInitialTabState([])),
                () => setSelectedFormByTab(createInitialTabState(null)),
                refresh,
                onHide
              )
            }
          },
        ]}
      />
      <AlertModal
        show={showSSNEINModal}
        onHide={() => setShowSSNEINModal(false)}
        title="Matched entered SSN/EIN with Multiple Clients"
        message={`${clientsMatchMessage}`}
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: () => setShowSSNEINModal(false) },
          {
            label: "Continue", onClick: () => {
              setOnDemandValidation(true);
              setContinueSavingClient(true);
              setShowSSNEINModal(false)
            }
          },
        ]}
      />

      {showMatchedSSNEINModal && (
        <MatchedSSNEINClients
          show={showMatchedSSNEINModal}
          onHide={() => setShowMatchedSSNEINModal(false)}
          matchedClients={clientsMatchedData}
          setContinueSavingClient={setContinueSavingClient}
          setOnDemandValidation={setOnDemandValidation}
        />
      )}

      <Modal
        show={show}
        onHide={handleAttemptClose}
        size="lg"
        backdrop="static"
        backdropClassName={styles.clientShellModalBackdrop}
        centered
        className={styles.saveClientModal}
        keyboard={false}
      >
        <Modal.Header className={styles.createClientModalHeader} closeButton>
          <span className={styles.createClientModalHeaderTitle}>
            Create Client
          </span>
        </Modal.Header>
        <Modal.Body className={styles.createClientModalBody}>
          <Row className={`no-gutter ${styles.creatorMainButtons}`}>
            <div className={styles.losContainer}>
              <h5 className={`${styles.losBadge}`}>
                <span className={styles.label}>Line of service:</span>
                <span className={styles.value}>{purpose?.value}</span>
              </h5>
            </div>

            {activeTab === "DOCUMENTS" && (
              <div className={styles.losContainer} style={{ marginLeft: 12 }}>
                <h5 className={`${styles.losBadge}`}>
                  <span className={styles.label}>Approver:</span>
                  <span className={styles.value}>
                    {firstApprover?.Displayname
                      ? firstApprover.Displayname
                      : "Not set"}
                  </span>
                </h5>
              </div>
            )}

            {formOptionsByTab[activeTab].length > 1 ? (
              <Form.Group style={{ marginRight: 20, width: "fit-content" }}>
                <Select
                  options={formOptionsByTab[activeTab]}
                  value={
                    selectedFormByTab[activeTab]
                      ? {
                        value: selectedFormByTab[activeTab]!.FirmFormId,
                        label: selectedFormByTab[activeTab]!.FormName,
                      }
                      : null
                  }
                  onChange={handleFormSelect}
                  isLoading={formApiProgress}
                  classNamePrefix="react-select"
                  placeholder="Select form"
                />
              </Form.Group>
            ) : (
              <></>
            )}
            {activeTab !== "DOCUMENTS" ? (
              <CustomButton
                content={activeTab === orderedFlow[0].key ? "Next" : "Next"}
                // disabled={!isFormValid}
                progress={fetchClientApiProgress || clientUpdateApiProgress}
                onclick={() => {
                  setOnDemandValidation(true);
                }}
              />
            ) : (
              <>
                <CustomButton
                  content={"Send for approval"}
                  progress={approvalApiProgress}
                  onclick={async () => {
                    // Validate documents first
                    if (!documentsValid) {
                      setShowDocumentWarning(true);
                      return;
                    }

                    const approved = await sendForApproval(
                      [{ ClientMasterId: clientMasterId }],
                      setApproalApiProgress
                    );
                    if (approved) {
                      setDirtyByTab(createInitialTabState(false));
                      setShowAlert(false);
                      handleClose(
                        () => setFormFieldsByTab(createInitialTabState([])),
                        () => setSelectedFormByTab(createInitialTabState(null)),
                        refresh,
                        onHide
                      );
                    }
                  }}
                />

                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={openApprovalSettings}
                  disabled={!clientMasterId}
                  title="Approval Setting"
                >
                  <Icon.Gear />
                </button>

                <CustomButton
                  content={"Save as draft"}
                  onclick={() => {
                    setDirtyByTab(createInitialTabState(false));
                    setShowAlert(false);
                    handleClose(
                      () => setFormFieldsByTab(createInitialTabState([])),
                      () => setSelectedFormByTab(createInitialTabState(null)),
                      refresh,
                      onHide
                    )
                  }
                  }
                />
              </>
            )}
          </Row>

          <div className={styles.tabsArea}>
            <Tabs
              activeKey={activeTab}
              onSelect={handleTabSelect}
              className="mb-3"
            >
              {formFlowConfig
                .filter((step) => step.enabled)
                .sort((a, b) => a.order - b.order)
                .map((step) => (
                  <Tab
                    eventKey={step.key}
                    title={step.label}
                    key={step.key}
                    disabled={!enabledTabs.includes(step.key)}
                    className={styles.formTab}
                  >
                    {activeTab === step.key && step.key === "DOCUMENTS" ? (
                      <DocumentUploader
                        fields={clientDetails}
                        clientMasterId={clientMasterId}
                        onValidationChange={handleDocumentValidationChange}
                        formFieldsByTab={formFieldsByTab}
                      />
                    ) : (
                      <>
                        {!formApiProgress && !selectedFormByTab[step.key] && (
                          <div className={styles.missingFormWarning}>
                            Please select a form
                          </div>
                        )}
                        {activeTab === step.key &&
                          selectedFormByTab[step.key] && (
                            <CygnusForm
                              formId={`create-form-${step.key}`}
                              fields={formFieldsByTab[step.key] ?? []}
                              onFieldsChange={updateFormFields}
                              onValidationChange={setIsFormValid}
                              onDemandValidation={onDemandValidation}
                              onDemandValidationComplete={handleOnDemandValidationComplete}
                              onFieldEvent={handleFieldEventFromForm}
                              provideDirtyApi={(api) =>
                                (dirtyApiRef.current = api)
                              }
                              onDropdownsLoaded={() => setFormApiProgress(false)}
                              fieldVariant={selectedFormByTab[step.key]?.InputStructure?.formSettings?.fieldVariant}
                              sectionVariant={selectedFormByTab[step.key]?.InputStructure?.formSettings?.sectionVariant}
                              sectionLayout={selectedFormByTab[step.key]?.InputStructure?.formSettings?.sectionLayout}
                              intakeRules={selectedFormByTab[step.key]?.InputStructure?.formSettings?.intakeRules}
                              onDirtyChange={(snap) => {
                                // snap can be whatever you designed, so keep this defensive.
                                // If your CygnusForm sends { isDirty: boolean } use that.
                                // If it sends boolean directly, handle that too.
                                const isDirty =
                                  typeof snap === "boolean"
                                    ? snap
                                    : Array.isArray((snap as any)?.ids)
                                      ? (snap as any).ids.length > 0
                                      : (snap as any)?.byId
                                        ? Object.values((snap as any).byId).some(Boolean)
                                        : false;

                                setDirtyByTab((prev) => ({ ...prev, [step.key as TabKey]: isDirty }));
                              }}
                            />
                          )}
                      </>
                    )}
                  </Tab>
                ))}
            </Tabs>
          </div>

          {formApiProgress && (
            <div className={styles.creatorLoader}>
              <ThreeCircles
                height="50"
                width="50"
                color="#4fa94d"
                wrapperStyle={{}}
                wrapperClass=""
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
              <div style={{ marginLeft: 10 }}>Loading your data</div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ClientCreator;
